import { useEffect, useRef, useState } from 'react';
import { DitheringShader, type DitheringShape, type DitheringType } from './dithering-shader.js';

interface Props {
  shape?: DitheringShape;
  type?: DitheringType;
  colorBack?: string;
  colorFront?: string;
  pxSize?: number;
  speed?: number;
  className?: string;
  /** Cap the rendered pixel buffer to avoid shader cost on huge hero heights */
  maxPixelRatio?: number;
}

/**
 * Responsive wrapper around the base `DitheringShader`. Observes its parent's
 * bounding rect and pipes live width/height to the shader so the canvas fills
 * whatever container you drop it into. Honours `prefers-reduced-motion` by
 * pinning `speed` to 0 (the shader then renders a single static frame).
 */
export function DitheringShaderFill({
  shape = 'swirl',
  type = '4x4',
  colorBack = '#0a0a0a',
  colorFront = '#2de1fc',
  pxSize = 3,
  speed = 0.9,
  className = '',
  maxPixelRatio = 1.25,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const listener = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const dpr = Math.min(maxPixelRatio, window.devicePixelRatio || 1);

    const update = (w: number, h: number) => {
      const wi = Math.max(1, Math.floor(w * dpr));
      const hi = Math.max(1, Math.floor(h * dpr));
      setSize((prev) => (prev && prev.w === wi && prev.h === hi ? prev : { w: wi, h: hi }));
    };

    const rect = el.getBoundingClientRect();
    update(rect.width, rect.height);

    // ResizeObserver is absent on old Android WebView / some jsdom configs.
    // Fall back to a static size snapshot so the canvas still renders.
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      update(entry.contentRect.width, entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxPixelRatio]);

  return (
    <div ref={containerRef} className={`absolute inset-0 ${className}`} aria-hidden="true">
      {size && (
        <DitheringShader
          width={size.w}
          height={size.h}
          shape={shape}
          type={type}
          colorBack={colorBack}
          colorFront={colorFront}
          pxSize={pxSize}
          speed={reducedMotion ? 0 : speed}
          style={{ width: '100%', height: '100%' }}
        />
      )}
    </div>
  );
}
